import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { FeedCommentItem } from "../../../shared/contracts/product-pages.js";
import { reportComment, setBlock } from "./safety-api.js";
import { SafetyReasonField } from "./safety-reason-field.js";
import { useSafetyOnline } from "./use-safety-online.js";
import "../../styles/social-safety.css";
export function CommentSafety({
  comment,
}: {
  comment: FeedCommentItem;
}): React.JSX.Element {
  const online = useSafetyOnline();
  const [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [sent, setSent] = useState(false);
  const request = useRef<{ reason: string; id: string } | null>(null);
  async function report(): Promise<void> {
    if (busy || !reason.trim() || !navigator.onLine) return;
    const clean = reason.trim();
    if (request.current?.reason !== clean)
      request.current = { reason: clean, id: crypto.randomUUID() };
    setBusy(true);
    setMessage("");
    try {
      await reportComment(comment.id, request.current.id, clean);
      setSent(true);
      setMessage("Report saved for administrator review.");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Report could not be saved. Retry the same report.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function block(): Promise<void> {
    if (busy || !navigator.onLine) return;
    setBusy(true);
    try {
      await setBlock(comment.accountId, true);
      window.location.reload();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Block could not be saved.",
      );
      setBusy(false);
    }
  }
  return (
    <details className="fr-comment-safety">
      <summary>Report or block</summary>
      {!sent ? (
        <>
          <SafetyReasonField
            label="Reason for reporting"
            value={reason}
            onChange={setReason}
          />
          <Button
            variant="link"
            disabled={busy || !reason.trim() || !online}
            onClick={() => void report()}
          >
            Report comment
          </Button>
        </>
      ) : null}
      <p>
        Blocking stops rival access and hides each other’s comments. Your own
        played scorecards stay intact. Unblocking does not restore the rivalry.
      </p>
      <Button
        variant="link"
        disabled={busy || !online}
        onClick={() => void block()}
      >
        Block {comment.displayName}
      </Button>
      {message ? <p role={sent ? "status" : "alert"}>{message}</p> : null}
    </details>
  );
}
